import React, { useState, useEffect, useMemo } from 'react';
import { useFilters } from '../../../context/FilterContext';
import '../../mentorComponents/labPractice/LabPractice.css';
import { getAllCOE } from '../../../services/adminServices/CoeServices';
import { toast } from 'react-toastify';
import {
  Box, Button, Select, MenuItem, FormControl, InputLabel, CircularProgress,
  FormHelperText, TablePagination, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Typography, Autocomplete, TextField
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { tableCellClasses } from "@mui/material/TableCell";
import { getAllMakerPlanDetails, getMakerPlanByLabType } from '../../../services/adminServices/MakerPlanServices';
import { getLabsByLabtype, getLabWiseTopicPractice } from '../../../services/reportServices/adminReportServices/LabWiseReportServices';
import { getLabType } from '../../../services/mentorServices/MentorServices';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#7e7e7e",
    color: theme.palette.common.white,
    fontSize: 16,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const AdminDailyPracticeReport = () => {
  const { adminDailyPracticeState, setAdminDailyPracticeState } = useFilters();

  const [selection, setSelection] = useState(() => {
      if (adminDailyPracticeState.selection && adminDailyPracticeState.selection.coe) {
          return adminDailyPracticeState.selection;
      }
      const user = JSON.parse(localStorage.getItem('user')) || {};
      return { lab: '', coe: user.coeId || user.coe_id || '', makerPlan: '', makerModule: '', labType: "" };
  });
  const [tableData, setTableData] = useState(adminDailyPracticeState.tableData);
  const [loading, setLoading] = useState(false);
  const [disableButton, setDisableButton] = useState(true);
  const [errors, setErrors] = useState({});
  const [options, setOptions] = useState(adminDailyPracticeState.options || { coes: [], labNames: [], makerPlanOptions: [], moduleOptions: [], labTypeOptions: [] });

  const [page, setPage] = useState(adminDailyPracticeState.page);
  const [rowsPerPage, setRowsPerPage] = useState(adminDailyPracticeState.rowsPerPage);
  const [showTable, setShowTable] = useState(adminDailyPracticeState.showTable);

  useEffect(() => {
      setAdminDailyPracticeState({
          selection, tableData, showTable, page, rowsPerPage, options, hasFetched: adminDailyPracticeState.hasFetched
      });
  }, [selection, tableData, showTable, page, rowsPerPage, options, setAdminDailyPracticeState, adminDailyPracticeState.hasFetched]);

  useEffect(() => {
    if (!options.coes || options.coes.length === 0) {
      getAllCOE()
        .then((res) => {
          const sortedLabs = res.data.data?.sort((a, b) => a.name?.localeCompare(b.name)) || [];
          setOptions(prev => ({ ...prev, coes: sortedLabs || [] }))
        })
        .catch(err => toast.error(err.message || err.response.data.message || "Failed to fetch COEs"));
    }

    if (!options.labTypeOptions || options.labTypeOptions.length === 0) {
      getLabType()
        .then(res => setOptions(prev => ({ ...prev, labTypeOptions: res.data.data.value || [] })))
        .catch(err => toast.error(err.message || err.response.data.message || "Failed to fetch labTypes"));
    }
  }, []);

  const handleSelectionChange = async ({ target: { name, value } }) => {

    setSelection(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));

    try { if (name === 'coe') {
      setOptions(prev => ({ ...prev, makerPlanOptions:  [], moduleOptions: [], labNames: [] ,  }));
      setSelection(prev => ({ ...prev, makerPlan: '', makerModule: '', lab: '', labType: '' }));
    }
      if (name === 'labType') {
        const res = await getMakerPlanByLabType(selection.coe, value);
        setOptions(prev => ({ ...prev, makerPlanOptions: res.data.data || [], moduleOptions: [], labNames: [] }));
        setSelection(prev => ({ ...prev, makerPlan: '', makerModule: '', lab: '' }));
      }
      if (name === 'makerPlan') {
        const res = await getAllMakerPlanDetails(value);
        setOptions(prev => ({ ...prev, moduleOptions: res?.data?.data?.Maker_Modules || [] }));
        setSelection(prev => ({ ...prev, makerModule: '' }));
        if (!value) { setOptions(prev => ({ ...prev, labNames: [] })); }
        else {
          const res1 = await getLabsByLabtype(selection.coe, selection.labType, value);
          setOptions(prev => ({ ...prev, labNames: res1.data.data || [] }));
        }

      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || `Error fetching ${name} data`);
    }

  };
  useEffect(() => {
    if (selection.coe && selection.labType && selection.makerPlan && selection.makerModule) {
      setDisableButton(false)
    } else {
      setDisableButton(true)
    }

  }, [selection])
  const handleLabChange = (_, newValue) => {
    setSelection(prev => ({ ...prev, lab: newValue?.labID || '' }));
    setErrors(prev => ({ ...prev, lab: '' }));
  };
  const fetchData = async () => {
    const requiredFields = ['coe', 'makerPlan', 'makerModule', 'labType'];
    const missingFields = requiredFields.filter(field => !selection[field]);
    if (missingFields.length) {
      const newErrors = {};
      missingFields.forEach(key => {
        newErrors[key] = `Please select a ${key.replace(/([A-Z])/g, ' $1')}`;
      });
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const { coe, lab, makerPlan, makerModule, labType } = selection;
      const res = await getLabWiseTopicPractice(coe, labType, makerPlan, makerModule, lab);
      setTableData(res?.data?.data || []);
      setShowTable(true);
      setAdminDailyPracticeState(prev => ({ ...prev, hasFetched: true }));
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || 'Error fetching report');
      setTableData([]);
    } finally {
      setLoading(false);
    }
  };

  const sortedData = useMemo(() => [...tableData].sort((a, b) => new Date(b.AttendanceDate) - new Date(a.AttendanceDate)), [tableData]);
  const paginatedRows = useMemo(() => sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [sortedData, page, rowsPerPage]);

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = e => { setRowsPerPage(+e.target.value); setPage(0); };

  const fieldConfigs = [
    { key: 'coe', optionsKey: 'coes', labelKey: 'name', idKey: 'id' },
    { key: 'labType', optionsKey: 'labTypeOptions', labelKey: "lab_type" },
    { key: 'makerPlan', optionsKey: 'makerPlanOptions', labelKey: 'makerplan_name', idKey: 'makerplanID' },
    { key: 'makerModule', optionsKey: 'moduleOptions', labelKey: 'module_Name', idKey: 'moduleID' }
  ];

  return (
    <Box className="dashboard-container" sx={{ justifyContent: "start" }}>
      <Typography className="dashboard-header-title" pt={2} pb={2} width="100%">
        Welcome {JSON.parse(localStorage.getItem("user"))?.name || "User"}! to the Daily Practice Report.
      </Typography>
      <Box display="flex" justifyContent="flex-start" alignItems="start" flexWrap="wrap" minWidth="100% !important" gap={1}>
        {fieldConfigs.map(({ key, optionsKey, labelKey, idKey }) => (
          <FormControl key={key} size="small" sx={{ width: "24%" }} error={!!errors[key]}>
            <InputLabel>{key?.replace(/([A-Z])/g, ' $1')?.replace(/^./, s => s.toUpperCase())}*</InputLabel>
            <Select name={key} value={selection[key]} label={key} onChange={handleSelectionChange}>
              <MenuItem value="">None</MenuItem>
              {options[optionsKey].map((opt, i) => (
                <MenuItem key={i} value={opt[idKey] || opt}>{opt[labelKey] || opt}</MenuItem>
              ))}
            </Select>
            {errors[key] && <FormHelperText>{errors[key]}</FormHelperText>}
          </FormControl>
        ))}

        {/* LAB Autocomplete */}
        <Autocomplete
          sx={{ width: "24%" }}
          size="small"
          options={options.labNames?.sort((a, b) =>
            a.lab_name?.localeCompare(b.lab_name)
          )}
          getOptionLabel={(option) => option.lab_name || ''}
          value={options.labNames.find(lab => lab.labID === selection.lab) || null}
          onChange={handleLabChange}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Lab"
              error={!!errors.lab}
              helperText={errors.lab}
            />
          )}
        />

        <Button variant="contained" onClick={fetchData} disabled={loading || disableButton}>
          {loading ? <CircularProgress size={24} /> : 'VIEW'}
        </Button>
      </Box>

      <Box className="dashboard-box-container">
        {showTable && (
          <TableContainer component={Paper} className="table-container" sx={{ width: "100%", overflowX: "auto" }}>
            <Table sx={{ tableLayout: "auto", width: "max-content", minWidth: "100%" }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Lab Name</StyledTableCell>
                  <StyledTableCell>Lab Strength</StyledTableCell>
                  <StyledTableCell>Not Recorded</StyledTableCell>
                  {tableData[0]?.topic_started_by_learners?.map((d, i) => (
                    <StyledTableCell key={i}>#{d.name} Started</StyledTableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.length ? (paginatedRows.map((row, i) => (
                  <TableRow key={i}>
                    <TableCell>{row.lab_name || '-'}</TableCell>
                    <TableCell>{row.lab_strength || 0}</TableCell>
                    <TableCell>{row.not_started || 0}</TableCell>
                    {row.topic_started_by_learners.map((d, j) => (
                      <TableCell key={j}>{d.started}</TableCell>
                    ))}
                  </TableRow>
                ))) : (<TableRow>
                  <StyledTableCell colSpan={20} align="center">
                    No records found.
                  </StyledTableCell>
                </TableRow>
                )}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10,20,30,60,100]}
              count={tableData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              component="div"
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default AdminDailyPracticeReport;
